import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { auth } from '../firebase/config';
import { useSession } from '../hooks/useSession';
import { useCursor } from '../hooks/useCursor';
import { PALETTE } from '../types';
import Canvas from '../components/canvas/Canvas';
import Sidebar from '../components/sidebar/Sidebar';
import LegendPanel from '../leyend/LegendPanel';
import NodeAddPopup from '../components/popups/NodeAddPopup';
import NodeEditPopup from '../components/popups/NodeEditPopup';
import SegmentEditor from '../components/popups/SegmentEditor';

// ── Tipos de popup ────────────────────────────────────────────────────────────

interface NodeAddState { svgX: number; svgY: number; screenX: number; screenY: number; }
interface NodeEditState { nodeId: string; screenX: number; screenY: number; }
interface SegEditState { procId: number; segIdx: number; }
interface SegHoverState { procId: number; segIdx: number; }

// ── Session page ──────────────────────────────────────────────────────────────

const Session: React.FC = () => {
    const { sessionId = '' } = useParams<{ sessionId: string }>();
    const user = auth.currentUser;
    const anonUidRef = useRef('anon-' + Math.random().toString(36).slice(2, 8));
    const cursorIdRef = useRef('cursor-' + Math.random().toString(36).slice(2, 10));
    const uid = user?.uid ?? anonUidRef.current;
    const cursorId = cursorIdRef.current;

    // Asigna color de cursor según posición en PALETTE (hash simple del uid)
    const colorIndex = uid.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length;

    // SVG ref para useCursor
    const svgRef = useRef<SVGSVGElement>(null);

    // ── Firebase sync ────────────────────────────────────────────────────────────

    const { loading, error } = useSession({
        sessionId,
        uid,
        cursorId,
        syncEnabled: true,
    });

    // ── Cursor propio ─────────────────────────────────────────────────────────

    useCursor({
        sessionId,
        uid,
        displayName: user?.displayName ?? user?.email ?? uid.slice(0, 6),
        svgRef,
        colorIndex,
    });

    // ── Popup state ───────────────────────────────────────────────────────────

    const [nodeAdd, setNodeAdd] = useState<NodeAddState | null>(null);
    const [nodeEdit, setNodeEdit] = useState<NodeEditState | null>(null);
    const [segEdit, setSegEdit] = useState<SegEditState | null>(null);
    const [segHover, setSegHover] = useState<SegHoverState | null>(null);

    const closeAll = () => { setNodeAdd(null); setNodeEdit(null); setSegEdit(null); };
    const hoveredSegmentKey = segHover ? `${segHover.procId}:${segHover.segIdx}` : null;

    // ── Loading / error states ────────────────────────────────────────────────

    if (loading) return <Centered>Cargando sesión…</Centered>;
    if (error) return <Centered style={{ color: '#c0392b' }}>{error}</Centered>;

    return (
        <div
            style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', background: '#f5f2ec', fontFamily: "'DM Mono',monospace" }}
            onClick={closeAll}
        >
            <div style={{ position: 'absolute', inset: 0 }}>
                <Canvas
                    svgRef={svgRef}
                    hoveredSegmentKey={hoveredSegmentKey}
                    onSegmentClick={(procId, segIdx) => { closeAll(); setSegEdit({ procId, segIdx }); }}
                    onSegmentHover={(procId, segIdx, hovered) => {
                        setSegHover(hovered ? { procId, segIdx } : null);
                    }}
                    onNodeDblClick={(nodeId, x, y) => { closeAll(); setNodeEdit({ nodeId, screenX: x, screenY: y }); }}
                    onCanvasRightClick={(sx, sy, cx, cy) => { closeAll(); setNodeAdd({ svgX: sx, svgY: sy, screenX: cx, screenY: cy }); }}
                />
            </div>
 

            <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, zIndex: 20 }}>
                <Sidebar
                    svgRef={svgRef}
                    hoveredSegmentKey={hoveredSegmentKey}
                    onSegmentHover={(procId, segIdx, hovered) => setSegHover(hovered ? { procId, segIdx } : null)}
                    onSegmentEdit={(procId, segIdx) => { closeAll(); setSegEdit({ procId, segIdx }); }}
                />
            </div>

            {nodeAdd && (
                <NodeAddPopup {...nodeAdd} onClose={() => setNodeAdd(null)} />
            )}
            {nodeEdit && (
                <NodeEditPopup {...nodeEdit} onClose={() => setNodeEdit(null)} />
            )}
            {segEdit && (
                <SegmentEditor
                    {...segEdit}
                    highlight={segHover?.procId === segEdit.procId && segHover?.segIdx === segEdit.segIdx}
                    onClose={() => setSegEdit(null)}
                />
            )}
        </div>
    );
};

const Centered: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f2ec', fontFamily: "'DM Mono',monospace", fontSize: 16, color: '#888', ...style }}>
        {children}
    </div>
);

export default Session;
